(ns app.front.view.search.mobile
  (:require [app.domain.property.model :as property-model]
            [app.front.dashboard.search :as search]
            [app.front.search-counts :as counts]
            [app.front.ui :as ui]
            [app.front.ui.filter-common :as filter-common]
            [app.front.ui.radio :as ui-radio]
            [app.front.view.property.dashboard.widgets.filters :as property-filters]
            [app.front.view.property.dashboard.widgets.search :as property-search]
            [app.front.view.search.saved-searches :as saved-searches]
            [app.front.view.search.subs :as subs]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [reagent.core :as r]))

(defn sort-label [tr property? [attr direction]]
  (if property?
    (str (tr [attr]) " — " (tr [(if (= :asc direction) :search/sort-ascending :search/sort-descending)]))
    (tr [(keyword "search-sorting" (str (name attr) "-" (name direction)))])))

(defn- sort-state [property?]
  (if property?
    (let [config (<sub [::property-search/config])]
      {:value     (<sub [::property-search/initial-sorting])
       :default   (:sort-condition config)
       :options   (for [attr (:sortable-attrs config) direction [:asc :desc]] [attr direction])
       :set-event ::subs/set-property-sort})
    (let [config (<sub [::search/search-config])]
      {:value     (<sub [::search/sort-condition])
       :default   (:sort-condition config)
       :options   (:sort-options config)
       :set-event ::search/set-sort-condition})))

(defn- filter-chips [tr property?]
  (if property?
    (let [templates (<sub [::property-search/property-attribute-templates])
          states    (<sub [::property-search/state-filter])]
      (concat
        (when (seq states)
          [{:id     :status
            :label  (tr [:property/state-filter])
            :remove #(>evt [::property-search/set-state-filter #{}])}])
        (keep-indexed
          (fn [idx {:keys [template-id value]}]
            (when-not (str/blank? value)
              {:id     [:attribute idx]
               :label  (some->> templates
                                (filter #(= template-id (:db/id %)))
                                first
                                property-model/attribute-template-name)
               :remove #(>evt [::property-filters/remove-attribute-filter idx [::property-search/search]])}))
          (<sub [::property-filters/attribute-filters]))))
    (->> (<sub [::search/filter-values])
         (sort-by (comp str key))
         (keep (fn [[attr values]]
                 (when (seq values)
                   {:id     attr
                    :label  (filter-common/filter-label tr attr)
                    :remove #(>evt [::search/set-filter-value attr #{}])}))))))

(defn- selection-chips [tr property?]
  (let [{:keys [value default set-event]} (sort-state property?)]
    (concat
      (when (and value (not= value default))
        [{:id     :sort
          :label  (sort-label tr property? value)
          :remove #(>evt [set-event default])}])
      (filter-chips tr property?))))

(defn- chips-row [tr chips]
  (when (seq chips)
    [:div.flex.flex-wrap.gap-2.my-4 {:data-cy "mobile-search-chips"}
     (for [{:keys [id label remove]} chips]
       ^{:key (str id)}
       [:button.border.border-solid.border-ui-night-sky-100.rounded.bg-white.px-2.py-1.text-ui-night-sky-100
        {:type       "button"
         :on-click   remove
         :aria-label (tr [:search/remove-filter] [label])}
        [:span.mr-2 {:aria-hidden true} "×"] label])
     [:button.border.border-solid.border-ui-night-sky-100.rounded.bg-white.px-2.py-1.text-ui-night-sky-100
      {:type "button" :on-click #(>evt [::subs/clear-mobile-filters]) :data-cy "mobile-search-clear-all"}
      (tr [:search/clear-all])]]))

(defn- sort-controls [tr property?]
  (let [{:keys [value options set-event]} (sort-state property?)]
    (when (seq options)
      [:div.p-4.border-b.border-solid.border-ui-gray
       [:h2.text-xl.font-semibold (tr [:search/sort])]
       [ui-radio/radio {:name        "mobile-search-sort"
                        :values      options
                        :value       value
                        :val->str    #(sort-label tr property? %)
                        :on-change   #(>evt [set-event %])
                        :group-class "!flex-col !items-start gap-4"}]])))

(defn- filter-controls [tr property?]
  (if property?
    ;; Only the mobile or the desktop controls are mounted at a time.
    [property-search/filter-controls tr]
    (let [values     (<sub [::search/filter-values])
          conditions (concat (<sub [::search/button-filter-conditions])
                             (<sub [::search/selectable-filter-conditions]))]
      [filter-common/dashboard-filter-selects
       {:tr                       tr
        :mobile?                  true
        :light?                   false
        :config-loading?          (<sub [::search/config-loading?])
        :conditions               conditions
        :filter-values            values
        :revision                 (<sub [::search/revision])
        :val->str-fn              (<sub [::search/val->str-fn])
        :set-filter-value-evt     ::search/set-filter-value
        :set-date-range-bound-evt ::search/set-date-range-bound}])))

(defn- save-button [tr close!]
  (when (and (not= :complaint (<sub [::subs/selected-entity-tab]))
             (ff/enabled? :feature-flag.id/saved-searches))
    [ui/button {:kind       :secondary
                :class-name "w-full max-w-none justify-center uppercase mt-6"
                :data-cy    "mobile-save-search"
                :disabled   (not (saved-searches/save-enabled? (<sub [::subs/save-data])))
                :on-click   (fn []
                              (close!)
                              (r/after-render #(>evt [::saved-searches/open-save-dialog])))}
     (tr [:saved-search/save-this-search])]))

(defn- panel-content [tr property? close!]
  (let [tab      (<sub [::subs/selected-entity-tab])
        total    (<sub [(if property? ::property-search/result-count ::search/result-total-count)])
        loading? (or (<sub [::counts/loading? tab])
                     (<sub [(if property? ::property-search/loading? ::search/config-loading?)]))]
    [:<>
     [:header.flex.items-center.justify-between.p-4.border-b.border-solid.border-ui-gray
      [:h2.text-xl.font-semibold.mb-0 (tr [:search/sort-and-filter])]
      [:button.border-0.bg-white.text-2xl.w-10.h-10
       {:type "button" :on-click close! :aria-label (tr [:close]) :data-cy "mobile-filter-close"}
       "×"]]
     [:div.overflow-y-auto.flex-1.pb-6
      [sort-controls tr property?]
      [:div.p-4
       [:h2.text-xl.font-semibold (tr [:search/filters])]
       [:p {:role "status" :aria-live "polite" :data-cy "mobile-filter-count"}
        (if loading?
          (tr [:search/loading])
          (tr [(keyword "search" (str "filtering-" (name tab)))] [(or total 0)]))]
       [filter-controls tr property?]
       [save-button tr close!]]]]))

(defn- full-screen-panel [_tr _property? _close!]
  (let [*dialog         (atom nil)
        *previous-focus (atom nil)
        *overflow       (atom nil)]
    (r/create-class
      {:display-name "mobile-search-filter-panel"
       :component-did-mount
       (fn [_]
         (reset! *previous-focus (.-activeElement js/document))
         (reset! *overflow (.. js/document -body -style -overflow))
         (set! (.. js/document -body -style -overflow) "hidden")
         (.showModal ^js @*dialog))
       :component-will-unmount
       (fn [_]
         (.close ^js @*dialog)
         (set! (.. js/document -body -style -overflow) @*overflow)
         (when (.-isConnected ^js @*previous-focus)
           (.focus ^js @*previous-focus)))
       :reagent-render
       (fn [tr property? close!]
         [:dialog.fixed.inset-0.m-0.p-0.border-0.bg-white.text-ui-night-sky-100.w-full.max-w-none.max-h-none
          {:ref        #(reset! *dialog %)
           :style      {:height "100dvh"}
           :aria-label (tr [:search/sort-and-filter])
           :data-cy    "mobile-search-filter-panel"
           :on-cancel  (fn [event] (.preventDefault event) (close!))}
          [:div.flex.flex-col.h-full
           [panel-content tr property? close!]]])})))

(defn toolbar [tr]
  (r/with-let [*open? (r/atom false)]
    (let [property? (= :property (<sub [::subs/selected-entity-tab]))
          total     (<sub [(if property? ::property-search/result-count ::search/result-total-count)])
          chips     (selection-chips tr property?)]
      [:div.mb-6
       [:div.flex.items-center.justify-between.gap-3
        [:h3.mb-0.font-semibold (tr [:search/results-with-count] [(or total 0)])]
        [ui/button {:kind       :secondary
                    :size       :small
                    :class-name "whitespace-nowrap"
                    :data-cy    "mobile-sort-filter"
                    :on-click   #(reset! *open? true)}
         (tr [:search/sort-and-filter])
         (when (seq chips)
           [:span.ml-2.rounded-full.bg-ui-violet-100.text-white.px-2 {:data-cy "mobile-filter-badge"}
            (count chips)])]]
       [chips-row tr chips]
       (when @*open?
         [full-screen-panel tr property? #(reset! *open? false)])])))
